import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { PostCard } from '@/components/PostCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Loader2, Plane, TrendingUp } from 'lucide-react';

const TRENDING_SEARCHES = [
  'Sunset', 'Mountains', 'Clouds', 'Wing', 'City lights', 
  'Ocean', 'Sunrise', 'Emirates', 'Boeing 787', 'Grand Canyon'
];

export default function Explore() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (query = searchQuery) => {
    if (!query.trim()) return;
    
    setLoading(true);
    setHasSearched(true);
    setSearchParams({ q: query });

    try {
      const response = await axios.get(`${API}/posts/search`, {
        params: { q: query },
        withCredentials: true
      });
      setPosts(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTrendingClick = (term) => {
    setSearchQuery(term);
    handleSearch(term);
  };

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setSearchQuery(q);
      handleSearch(q);
    }
  }, []);

  const handleLikeUpdate = (postId, liked, count) => {
    setPosts(posts.map(p => 
      p.post_id === postId 
        ? { ...p, is_liked: liked, likes_count: count }
        : p
    ));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="explore-page">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 
          className="text-4xl sm:text-5xl font-bold text-white mb-4"
          style={{ fontFamily: "'Space Grotesk', sans-serif" }}
        >
          Explore Views
        </h1>
        <p className="text-gray-400 max-w-xl mx-auto">
          Search for stunning window views by location, airline, aircraft, or description
        </p>
      </div>

      {/* Search Bar */}
      <div className="max-w-2xl mx-auto mb-12">
        <form 
          onSubmit={(e) => { e.preventDefault(); handleSearch(); }}
          className="relative"
        >
          <div className="search-input-wrapper">
            <Search className="w-5 h-5" />
            <Input
              type="text"
              placeholder="Search views... (e.g., 'Grand Canyon', 'Emirates', 'Boeing 787')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-ventana pl-12 pr-24 py-6 text-lg rounded-full"
              data-testid="search-input"
            />
          </div>
          <Button 
            type="submit"
            className="btn-primary-gradient absolute right-2 top-1/2 -translate-y-1/2 rounded-full px-6"
            disabled={loading}
            data-testid="search-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </Button>
        </form>

        {/* Trending Searches */}
        <div className="mt-6">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-gray-400">Trending searches</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {TRENDING_SEARCHES.map((term) => (
              <button
                key={term}
                onClick={() => handleTrendingClick(term)}
                className="px-4 py-2 rounded-full text-sm bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10 hover:border-purple-500/30 transition-colors"
                data-testid={`trending-${term.toLowerCase().replace(' ', '-')}`}
              >
                {term}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      ) : hasSearched ? (
        posts.length > 0 ? (
          <div className="bento-grid" data-testid="search-results">
            {posts.map((post, index) => (
              <PostCard 
                key={post.post_id} 
                post={post} 
                onLikeUpdate={handleLikeUpdate}
                variant={index % 5 === 0 ? 'tall' : 'default'}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto mb-6">
              <Plane className="w-10 h-10 text-purple-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">No views found</h2>
            <p className="text-gray-400">Try a different search term</p>
          </div>
        )
      ) : (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-full bg-purple-900/30 flex items-center justify-center mx-auto mb-6">
            <Search className="w-10 h-10 text-purple-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Start exploring</h2>
          <p className="text-gray-400">Search for window views or click a trending topic</p>
        </div>
      )}
    </div>
  );
}

export { Explore };
